import { Button, Flex, IconFont, Menu, pointer, Text, tinyButtons, Tooltip, usePopoverHeight } from '@byecode/ui'
import type { FieldInputADTValue, PhoneNumberConfig, TextValue } from '@lighthouse/core'
import { getPasteSmsCode } from '@lighthouse/tools'
import { reduce } from 'rambda'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useUpdateEffect } from 'react-use'
import styled from 'styled-components'

import { COUNTRY_LIST, FieldIconTypeMap, mobileRegex } from '../../../constants'
import { useSmsSendCode } from '../../../hooks'
import { useAppThemeContext } from '../../ApplicationContainer'
import { applyFillPickerValue4CssColorValue } from '../../FillPicker'
import BaseInput from '../BaseInput'
import { fieldLabelMap, popoverMaxHeight, sizeFontWeighMap, sizeMap } from '../constant'
import { useFieldInputFont } from '../hooks/useFieldInputFont'
import type { FieldBaseProps, Size } from '../types'

interface PhoneNumberFieldProps extends FieldBaseProps {
    value: TextValue
    phoneNumberConfig: PhoneNumberConfig
}

const SCxIconWrapper = styled.div`
    display: flex;
    align-items: center;
    ${pointer}
`

const SCxContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`

const SCxListContainer = styled.div`
    max-height: 600px;
    width: 100%;
    height: 100%;
    overflow: hidden auto;
    ${tinyButtons}
`

const SCxCodeInput = styled.input<{ textSize: Size; customFontSize?: string; customFontWeight?: number; placeholderColor?: string }>`
    width: 100%;
    height: 100%;
    outline: none;
    font-size: ${({ textSize, customFontSize }) => customFontSize || `${sizeMap[textSize]}px`};
    font-weight: ${({ textSize, customFontWeight }) => customFontWeight ?? sizeFontWeighMap[textSize]};
    line-height: ${({ customFontSize }) => (customFontSize ? 'normal' : '40px')};
    resize: none;
    overflow: hidden;
    word-break: break-word;
    min-height: 40px;
    padding-left: 8px;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    color: inherit;
    /* color: transparent; */
    cursor: inherit;

    ::placeholder {
        font-weight: ${({ textSize, customFontWeight }) => customFontWeight ?? sizeFontWeighMap[textSize]};
        color: ${({ placeholderColor }) => placeholderColor || 'var(--color-gray-300)'};
    }
`

type Country = {
    short: string
    name: string
    en: string
    tel: string
    flag: string
}

const PhoneNumberField: React.FunctionComponent<PhoneNumberFieldProps> = props => {
    const {
        onBlur,
        onChange,
        onFetchSmsCode,
        onChangeSmsCode,
        value,
        config: { size, placeholder, inputType, breakPoint },
        phoneNumberConfig,
        readOnly,
        onKeyDown,
        onFocus
    } = props
    const { theme } = useAppThemeContext()
    const { phoneNumber, autofocus } = phoneNumberConfig ?? {}
    const { isUsedAreaCode, areaCode: defaultAreaCode, selectAreaCode, isOpened } = phoneNumber ?? {}
    const inputRef = useRef<HTMLTextAreaElement>(null)
    const [isHover, setIsHover] = useState(false)
    const [isFocus, setIsFocus] = useState(false)
    const [isSmsFocus, setIsSmsFocus] = useState(false)
    const [opened, setOpened] = useState(false)

    const [areaCode, setAreaCode] = useState(defaultAreaCode)
    const [code, setCode] = useState('')
    const placeholderColor = applyFillPickerValue4CssColorValue(breakPoint?.design?.placeholderColor?.color, theme.palettes)
    const { customFontSize, customFontWeight } = useFieldInputFont(breakPoint)
    const handleGetCodeHandle = useCallback(
        async (resolve?: () => void, reject?: () => void) => {
            const result = await onFetchSmsCode?.(value)
            return result ? resolve?.() : reject?.()
        },
        [onFetchSmsCode, value]
    )
    const { getPhoneCode, reset, state: codeState } = useSmsSendCode(handleGetCodeHandle)

    const isShowSmsCode = isOpened && onFetchSmsCode

    const disabledSendSmsCodeBtn = codeState.codeStep > 0 || codeState.disable || readOnly || !mobileRegex.test(value)
    const countryList = useMemo(() => {
        return reduce<string, Country[]>(
            (pre, short) => {
                const option = COUNTRY_LIST.find(item => item.short === short)
                return option ? [...pre, { ...option, tel: `(+${option?.tel})` }] : pre
            },
            [],
            selectAreaCode ?? []
        )
    }, [selectAreaCode])

    const countryOptions = useMemo(() => {
        return (
            selectAreaCode?.map(short => {
                const country = countryList.find(item => item?.short === short)
                return {
                    label: country?.en,
                    value: country?.short ?? '',
                    tel: country?.tel ?? '',
                    icon: <img src={country?.flag} alt="" width={16} />,
                    extra: (
                        <Text color="var(--color-gray-400)" style={{ overflow: 'unset' }}>
                            {country?.tel}
                        </Text>
                    )
                }
            }) ?? []
        )
    }, [countryList, selectAreaCode])

    const [areaTelCodeValue, phoneValue] = useMemo(() => {
        const area = countryList.find(country => country?.short === areaCode)
        const countryTel = countryList.find(country => country?.tel && value.startsWith(country?.tel))
        if (!isUsedAreaCode) {
            return ['', value]
        }
        if (countryTel) {
            return [area?.tel ?? '', value.slice(countryTel.tel.length)]
        }
        return [area?.tel ?? '', value]
    }, [areaCode, countryList, isUsedAreaCode, value])

    const currentCountryOption = useMemo(
        () => countryOptions.find(item => item.tel === areaTelCodeValue),
        [areaTelCodeValue, countryOptions]
    )

    const { ref, height: maxHeight } = usePopoverHeight({ opened, popoverMaxHeight })

    useUpdateEffect(() => {
        setAreaCode(defaultAreaCode)
    }, [defaultAreaCode])

    const handleSmsCode = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setCode(v => {
                const smsCode = getPasteSmsCode(v, e.target.value).replace(/\D/g, '').slice(0, 6)
                onChangeSmsCode?.(smsCode)
                return smsCode
            })
        },
        [onChangeSmsCode]
    )

    const handleInputChange = useCallback(
        (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
            const { value } = ev.target
            const newText = value === '' ? '' : areaTelCodeValue + value
            onChange?.({ type: inputType, value: newText } as FieldInputADTValue)
        },
        [areaTelCodeValue, inputType, onChange]
    )

    const handleInputBlur = useCallback(() => {
        const newText = phoneValue === '' ? '' : areaTelCodeValue + phoneValue
        setIsFocus(false)
        onBlur?.({ type: inputType, value: newText } as FieldInputADTValue)
    }, [areaTelCodeValue, inputType, onBlur, phoneValue])

    const handleInputFocus = useCallback(() => {
        setIsFocus(true)
        onFocus?.()
    }, [onFocus])

    const handleAreaChange = useCallback(
        (short: string) => {
            const areaTelCode = countryList.find(country => country?.short === short)
            setAreaCode(areaTelCode?.short)
            setOpened(false)
            const newText = (areaTelCode?.tel ?? '') + phoneValue
            onBlur?.({ type: inputType, value: newText } as FieldInputADTValue)
        },
        [countryList, inputType, onBlur, phoneValue]
    )

    const href = useMemo(() => {
        if (!value) {
            return ''
        }
        return `tel:${value}`
    }, [value])

    const isShowRightSection = useMemo(() => (isHover && !readOnly) || isFocus, [isFocus, isHover, readOnly])

    const handleNavigate = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            e.stopPropagation()
            if (!href) {
                return
            }
            window.location.href = href
        },
        [href]
    )

    return (
        <SCxContainer>
            <BaseInput
                value={phoneValue}
                size={size}
                inputProps={{
                    autoFocus: autofocus,
                    placeholder,
                    onChange: handleInputChange,
                    onFocus: handleInputFocus,
                    onMouseLeave: () => {
                        setIsHover(false)
                    },
                    onBlur: handleInputBlur,
                    onKeyDown: ev => {
                        if (ev.key === 'Enter') {
                            ev.preventDefault()
                        }
                        onKeyDown?.(ev)
                    }
                }}
                hideText
                data-field-input
                data-field-active={opened || isFocus}
                data-field-disabled={!!readOnly}
                placeholderColor={placeholderColor}
                readOnly={readOnly}
                fontSize={customFontSize}
                fontWeight={customFontWeight}
                ref={inputRef}
                isWrap={false}
                leftSection={
                    isUsedAreaCode ? (
                        <Menu opened={opened} disabled={readOnly} closeOnItemClick width={200} withinPortal onChange={setOpened}>
                            <Menu.Target>
                                <div>
                                    <Flex style={{ marginRight: 8 }} ref={ref} gap={4} alignItems="center" justifyContent="flex-end">
                                        {currentCountryOption?.icon}
                                        {currentCountryOption ? (
                                            <Text>{areaTelCodeValue}</Text>
                                        ) : (
                                            <Text color={placeholderColor || 'var(--color-gray-300)'}>请选择</Text>
                                        )}
                                        <Flex alignItems="center" data-field-icon>
                                            <IconFont type="CareDown" size={16} />
                                        </Flex>
                                    </Flex>
                                </div>
                            </Menu.Target>
                            <Menu.Dropdown>
                                <SCxListContainer style={{ maxHeight }}>
                                    {countryOptions.map(option => (
                                        <Menu.Item
                                            key={option.value}
                                            icon={option.icon}
                                            onClick={() => handleAreaChange(option.value)}
                                            rightSection={
                                                option.value === areaTelCodeValue && (
                                                    <IconFont size={16} type="Tick" color="var(--color-app-main)" />
                                                )
                                            }
                                        >
                                            <Flex gap={4}>
                                                <Text lineClamp={1}> {option.label}</Text>
                                                {option.extra}
                                            </Flex>
                                        </Menu.Item>
                                    ))}
                                </SCxListContainer>
                            </Menu.Dropdown>
                        </Menu>
                    ) : null
                }
                rightSection={
                    isShowRightSection && (
                        <div
                            onMouseEnter={() => {
                                !readOnly && setIsHover(true)
                            }}
                            onMouseLeave={() => {
                                setIsHover(false)
                            }}
                            data-field-icon
                            style={{ display: 'flex', alignItems: 'center' }}
                        >
                            <Tooltip offset={16} title={fieldLabelMap.get(inputType) ?? ''}>
                                <SCxIconWrapper onClick={handleNavigate}>
                                    <IconFont type={FieldIconTypeMap[inputType]} />
                                </SCxIconWrapper>
                            </Tooltip>
                        </div>
                    )
                }
            />
            {isShowSmsCode && (
                <Flex gap={8} alignItems="center">
                    <SCxCodeInput
                        data-field-input
                        data-field-active={isSmsFocus}
                        data-field-disabled={!!readOnly}
                        readOnly={readOnly}
                        value={code}
                        textSize={size || 'middle'}
                        customFontSize={customFontSize}
                        customFontWeight={customFontWeight}
                        placeholderColor={placeholderColor}
                        placeholder="短信验证码"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={6}
                        onChange={handleSmsCode}
                        onFocus={() => setIsSmsFocus(true)}
                        onBlur={() => setIsSmsFocus(false)}
                        autoComplete="one-time-code"
                    />
                    <Button
                        data-field-extra
                        data-field-active={isSmsFocus}
                        data-field-disabled={!!readOnly || disabledSendSmsCodeBtn}
                        style={{ flexShrink: 0, height: 40, minWidth: 120, whiteSpace: 'nowrap' }}
                        onClick={() => {
                            if (!!readOnly || disabledSendSmsCodeBtn) {
                                return
                            }
                            getPhoneCode()
                        }}
                    >
                        {codeState.codeText}
                    </Button>
                </Flex>
            )}
        </SCxContainer>
    )
}

export default PhoneNumberField
